const DALE_API_NAMESPACE = "/v1";

/** Known local targets, also trusted by the dev-only auth and Vite guards. */
export const LOCAL_DALE_LITERAL_HOSTS: readonly string[] = [
  "localhost",
  "127.0.0.1",
  "[::1]",
];

/** Leading dots keep suffix matching within the actual tunnel domains. */
export const LOCAL_DALE_HOST_SUFFIXES: readonly string[] = [
  ".ngrok-free.app",
  ".ngrok-free.dev",
  ".ngrok.app",
  ".ngrok.io",
];

export const isNgrokOrigin = (origin: string): boolean => {
  try {
    const { hostname } = new URL(origin);
    return LOCAL_DALE_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
};

/** Local Dale, including ngrok tunnels, has no edge to strip `/v1`. */
export const isLocalDaleOrigin = (origin: string): boolean => {
  try {
    const { hostname } = new URL(origin);
    return (
      LOCAL_DALE_LITERAL_HOSTS.includes(hostname) ||
      LOCAL_DALE_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))
    );
  } catch {
    return false;
  }
};

const hasTerminalDaleNamespacePath = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url, "https://dale.invalid");
    return (
      !parsedUrl.search &&
      !parsedUrl.hash &&
      parsedUrl.pathname.endsWith(DALE_API_NAMESPACE)
    );
  } catch {
    return true;
  }
};

/**
 * Canonical raw Dale target shared by browser callers and the Vite proxy.
 * Ordinary API callers add the owned namespace separately; raw OAuth and
 * provider-ingress paths must never inherit it from configuration.
 */
export const normalizeDaleApiOrigin = (url: string | undefined): string => {
  const normalized = (url ?? "").trim().replace(/\/+$/, "");
  if (!normalized.endsWith(DALE_API_NAMESPACE)) return normalized;

  if (!hasTerminalDaleNamespacePath(normalized)) {
    return normalized;
  }

  return normalized.slice(0, -DALE_API_NAMESPACE.length).replace(/\/+$/, "");
};

export const withDaleApiNamespace = (url: string): string => {
  const rawOrigin = normalizeDaleApiOrigin(url);
  return rawOrigin ? `${rawOrigin}${DALE_API_NAMESPACE}` : rawOrigin;
};
